import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { RuntimeConfigService } from '../../../services/runtime-config.service';
import { BehaviorSubject, firstValueFrom, Observable, of } from "rxjs";
import { tap, catchError, filter, take, timeout } from "rxjs/operators";
import { HybridStorageKeys, HybridStorageService } from "../../services/hybrid-storage.service";
import { School } from "../../models/school.model";
import { UserService } from "../../../services/user-service.service";
import { ServiceStatus } from "./service-status.enum";
import { DirectoryService } from "../directory/directory.service";

@Injectable({
  providedIn: 'root'
})
export class SchoolsService {
  fetchSchoolServiceStatus: ServiceStatus = ServiceStatus.unset;
  private apiUrl: string;
  private schoolsCache = new BehaviorSubject<School[] | null>(null);
  // Schools for the read-only pickers (feed, calendar, directory, groups). Same as schoolsCache
  // except for CanSeeAllSchools holders, who get every school. Never use it for send targets.
  private viewableSchoolsCache = new BehaviorSubject<School[] | null>(null);
  // Emits whenever the selected school changes, so long-lived consumers (e.g. the AI chat
  // widget) can react to a directory-dropdown change without re-reading storage.
  private selectedSchoolSubject = new BehaviorSubject<School | null>(null);
  public selectedSchool$ = this.selectedSchoolSubject.asObservable();

  constructor(
    private http: HttpClient,
    private runtimeConfig: RuntimeConfigService,
    private hybridStorageService: HybridStorageService,
    private userService: UserService,
    private directoryService: DirectoryService
  ) {
    this.apiUrl = this.runtimeConfig.currentConfig.apiUrl;

    // Subscribe to config changes to update apiUrl
    this.runtimeConfig.config$.subscribe(config => {
      this.apiUrl = config.apiUrl;
    });
  }

    /**
   * Get a list of schools to be listed in the directory for a certain user
   * Uses cache-first strategy: returns cached data immediately if available,
   * then fetches fresh data in the background and updates cache
   */
  getSchoolsForUser(offlineMode: boolean = false): Observable<School[] | null> {
    if (!this.schoolsCache.value) {
      // Do NOT replace the BehaviorSubject here. Long-lived subscribers (e.g. the AI chat
      // widget, subscribed once in ngOnInit) hold the observable from the original instance;
      // swapping it out orphans them so they never receive the reloaded schools after a
      // clearCaches() — leaving the AI chat stuck "Connecting…". The field starts as a
      // BehaviorSubject and clearCaches() uses next(null), so reusing it is safe.
      this.loadCachedSchools();
    }

    // In offline mode the access token is expired and was never refreshed, so a network
    // fetch would only produce a 401. Serve cached schools and skip the authenticated call.
    if (!offlineMode) {
      this.fetchAndCacheSchools();
    }
    return this.schoolsCache.asObservable();
  }

  /**
   * Schools the user may browse on read-only screens: every school for CanSeeAllSchools holders,
   * otherwise the same list as getSchoolsForUser(). Not cached in storage — until the fetch
   * returns (or if it fails) it serves the user's own schools.
   */
  getViewableSchoolsForUser(): Observable<School[] | null> {
    if (!this.viewableSchoolsCache.value && this.schoolsCache.value) {
      this.viewableSchoolsCache.next(this.schoolsCache.value);
    }

    this.http.get<School[]>(this.apiUrl + "/school/GetViewableSchoolsAsync")
      .pipe(
        catchError(error => {
          console.error('Error fetching viewable schools:', error);
          return of(this.schoolsCache.value);
        })
      )
      .subscribe(schools => {
        if (schools) {
          this.viewableSchoolsCache.next(schools);
        }
      });
    return this.viewableSchoolsCache.asObservable();
  }

  /**
   * Resolve the user's schools once, waiting for a real list.
   *
   * getSchoolsForUser() is cache-first and its BehaviorSubject starts as null, so a caller that
   * takes the first emission (firstValueFrom) gets that null whenever the cache is cold — the
   * storage read and the network fetch are both still in flight. Callers that treat the result as
   * "the user has no schools" then silently do the wrong thing; the post template save modal, for
   * instance, hides its sharing control entirely and saves a template shared with nobody.
   *
   * Use this whenever the list is a one-shot input (dialog data, a save payload) rather than a
   * live binding. Resolves to null if nothing arrives within timeoutMs, so the caller can report
   * a failure instead of awaiting forever when both the cache and the fetch come up empty.
   */
  getSchoolsForUserOnce(timeoutMs: number = 10000): Promise<School[] | null> {
    return firstValueFrom(
      this.getSchoolsForUser().pipe(
        filter((schools): schools is School[] => !!schools && schools.length > 0),
        timeout({ first: timeoutMs, with: () => of(null) })
      ),
      { defaultValue: null }
    );
  }

  private async loadCachedSchools(): Promise<void> {
    try {
      const cachedData = await this.hybridStorageService.getItem<School[]>(
        HybridStorageKeys.Directory_schools
      );

      if (cachedData) {
        this.schoolsCache.next(cachedData);
      }
    } catch (error) {
      console.error('Error loading cached schools:', error);
    }
  }

  private fetchAndCacheSchools(): void {
    this.fetchSchoolServiceStatus = ServiceStatus.loading;
    this.http.get<School[]>(this.apiUrl + "/school/GetSchoolsByUserIdAsync/")
      .pipe(
        tap(response => {
          this.hybridStorageService.setItem(HybridStorageKeys.Directory_schools, response);
          this.schoolsCache.next(response);
          this.fetchSchoolServiceStatus = ServiceStatus.loaded;
        }),
        catchError(error => {
          this.fetchSchoolServiceStatus = ServiceStatus.error;
          console.error('Error fetching directory users:', error);
          return of(null);
        })
      )
      .subscribe();
  }

  /**
   * @param schoolList The list the selection must come from; defaults to the user's own schools.
   * The school selector passes the viewable list so a CanSeeAllSchools pick isn't reset.
   */
  async setSchoolFromList(schoolList?: School[]): Promise<School | null> {
    const storedSelectedSchool = await this.hybridStorageService.getItem<School>(HybridStorageKeys.Directory_SelectedSchool);
    const schoolFilterList = schoolList ?? this.schoolsCache.value ?? [];

    // Return null if no schools available
    if (schoolFilterList.length === 0) {
      console.warn('No schools available to select from');
      return null;
    }

    let selectedSchool: School | null = null;

    if (storedSelectedSchool && schoolFilterList.some(s => s.Id === storedSelectedSchool.Id)) {
      selectedSchool = storedSelectedSchool;
    } else {
      var currentUser = this.userService.CurrentUserProfile;
      const staffSchool = schoolFilterList.find(s => s.Id === currentUser?.StaffProfile?.SchoolId);
      if (staffSchool) {
        selectedSchool = staffSchool;
      } else {
        // Check for Service Center (1000)
        const serviceCenter = schoolFilterList.find(s => s.Id === 1000);
        if (serviceCenter) {
          selectedSchool = serviceCenter;
        } else {
          // Default to first school in list
          selectedSchool = schoolFilterList[0];
        }
      }

      // Save the newly selected school to storage
      if (selectedSchool) {
        await this.hybridStorageService.setItem(HybridStorageKeys.Directory_SelectedSchool, selectedSchool);
      }
    }

    return selectedSchool;
  }

  /**
   * Read-only companion to setSchoolFromList(): resolves the user's "current" school
   * using the SAME precedence (stored selection → staff home school → Service Center →
   * first) but WITHOUT persisting anything. For consumers like the AI widget that just
   * need the value and must not drive the directory's selection.
   * NOTE: keep the precedence here in sync with setSchoolFromList().
   */
  async getCurrentSchool(): Promise<School | null> {
    const storedSelectedSchool = await this.hybridStorageService.getItem<School>(HybridStorageKeys.Directory_SelectedSchool);
    const schoolFilterList = this.schoolsCache.value || [];

    if (schoolFilterList.length === 0) {
      return null;
    }

    if (storedSelectedSchool && schoolFilterList.some(s => s.Id === storedSelectedSchool.Id)) {
      return storedSelectedSchool;
    }

    const currentUser = this.userService.CurrentUserProfile;
    return schoolFilterList.find(s => s.Id === currentUser?.StaffProfile?.SchoolId)
      ?? schoolFilterList.find(s => s.Id === 1000)
      ?? schoolFilterList[0];
  }

  /**
   * Clear in-memory caches for schools and directory data.
   * Called during impersonation to ensure fresh data is loaded.
   */
  clearCaches(): void {
    this.schoolsCache.next(null);
    this.viewableSchoolsCache.next(null);
    this.selectedSchoolSubject.next(null);
    this.directoryService.clearCaches();
  }

  /**
   * Set the selected school and trigger directory data loading.
   * This should be called whenever the user or system changes the selected school.
   * @param school The school to select
   * @param forceRefresh If true, forces a fresh API call even if cache exists
   */
  async selectSchool(school: School, forceRefresh: boolean = false): Promise<void> {
    // Save the selected school to storage
    await this.hybridStorageService.setItem(HybridStorageKeys.Directory_SelectedSchool, school);
    this.selectedSchoolSubject.next(school);

    // Load directory data for this school
    const cacheKey = HybridStorageKeys.Directory_Users + school.Id;

    if (forceRefresh) {
      // Force a fresh load from the API
      this.directoryService.prefetchDirectoryUsers(school.Id);
    } else {
      // Check cache age and load if needed
      const { lastUpdated } = await this.hybridStorageService.getItemWithTimestamp(cacheKey);
      const sixtyMinutesInMs = 60 * 60 * 1000;
      const now = Date.now();
      const cacheAge = lastUpdated ? now - lastUpdated : Infinity;

      // Load if cache is older than 60 minutes or doesn't exist
      if (cacheAge > sixtyMinutesInMs) {
        this.directoryService.prefetchDirectoryUsers(school.Id);
      }
    }
  }

  /**
   * Initialize directory data:
   * 1. Load schools for user (if not cached)
   * 2. Set selected school (validates existing selection)
   * 3. Refresh directory cache if older than 60 minutes (background)
   */
  initializeDirectoryData(offlineMode: boolean = false): void {
    this.getSchoolsForUser(offlineMode)
      .pipe(
        filter(schools => schools !== null && schools.length > 0),
        take(1)
      )
      .subscribe(async schools => {
        if (!schools) return;

        // Set the selected school (validates existing selection or picks a new one)
        const selectedSchool = await this.setSchoolFromList();

        if (!selectedSchool) return;

        // In offline mode, stop here — the directory refresh below is also authenticated
        // and would 401 with the expired token. Cached directory data is used as-is.
        if (offlineMode) return;

        // Check if directory cache is older than 60 minutes
        const cacheKey = HybridStorageKeys.Directory_Users + selectedSchool.Id;
        const { lastUpdated } = await this.hybridStorageService.getItemWithTimestamp(cacheKey);

        const sixtyMinutesInMs = 60 * 60 * 1000;
        const now = Date.now();
        const cacheAge = lastUpdated ? now - lastUpdated : Infinity;

        // If cache is older than 60 minutes or doesn't exist, refresh it in the background
        if (cacheAge > sixtyMinutesInMs) {
          // This will fetch fresh data and update the cache in the background
          this.directoryService.prefetchDirectoryUsers(selectedSchool.Id);
        }
      });
  }
}
